import { WifiOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * OfflineIndicator — an unobtrusive banner shown only while the browser
 * reports no network connection. Pairs with the PWA service worker: when the
 * app is offline the precached shell still loads, and this strip tells the
 * operator that live data is stale.
 *
 * Kept deliberately minimal: a single fixed pill at the bottom-centre,
 * `role="status"` + `aria-live="polite"` so screen readers announce the
 * transition once, and no dependency on any provider beyond i18n.
 */
export function OfflineIndicator() {
  const { t } = useTranslation();
  const [online, setOnline] = useState(
    () => (typeof navigator === "undefined" ? true : navigator.onLine),
  );

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    // Re-sync in case the event fired before mount.
    setOnline(navigator.onLine);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  if (online) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-4 z-[70] flex justify-center pointer-events-none px-4"
    >
      <div className="pointer-events-auto inline-flex items-center gap-2 rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--bg-elevated)] px-3.5 py-2 text-[13px] font-medium text-[var(--fg)] shadow-[var(--shadow-3)]">
        <WifiOff size={15} strokeWidth={1.75} className="text-[var(--warning)] shrink-0" />
        <span>{t("common.offline")}</span>
      </div>
    </div>
  );
}
